import React, { useState, useRef, useEffect, useMemo, useCallback, Suspense, lazy } from 'react';
import RoleCard from './RoleCard';
import MarkdownViewer from '../../common/MarkdownViewer';
import './DeliverableContent.scss';

const MarkdownEditor = lazy(() => import('../../common/MarkdownEditor'));

const PRESET_ROLES = ['Frontend', '整合', 'Backend', 'QA'];

// ─── Handoff Popover ──────────────────────
const HandoffPopover = ({ anchorRef, candidates, currentAssigneeId, onSelect, onClear, onClose }) => {
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorRef]);

  if (!candidates.length) {
    return (
      <div className="dl-handoff-popover" ref={popoverRef}>
        <div className="dl-handoff-empty">尚無可交棒的對象（請先指派角色）</div>
      </div>
    );
  }

  return (
    <div className="dl-handoff-popover" ref={popoverRef}>
      <div className="dl-handoff-title">交棒給</div>
      {candidates.map((c) => (
        <button
          key={c.userId}
          className={`dl-handoff-option ${c.userId === currentAssigneeId ? 'current' : ''}`}
          onClick={() => onSelect(c)}
        >
          {c.avatarUrl ? (
            <img className="dl-handoff-avatar" src={c.avatarUrl} alt="" />
          ) : (
            <span className="dl-handoff-avatar-placeholder">
              {(c.name || c.email || '?')[0].toUpperCase()}
            </span>
          )}
          <span className="dl-handoff-name">{c.name || c.email}</span>
          <span className="dl-handoff-role">{c.roleType}</span>
        </button>
      ))}
      {currentAssigneeId && (
        <button className="dl-handoff-option dl-handoff-clear" onClick={onClear}>
          <span className="dl-handoff-clear-icon">✕</span>
          <span className="dl-handoff-name">清除交棒</span>
        </button>
      )}
    </div>
  );
};

const DeliverableContent = ({
  deliverable,
  roleGroups,
  usersMap,
  onQaToggle,
  onOpenPanel,
  onAddRoleSection,
  onDeleteRole,
  onDescriptionChange,
  onHandoff,
}) => {
  const [addingRole, setAddingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [showHandoff, setShowHandoff] = useState(false);
  const roleInputRef = useRef(null);
  const handoffBtnRef = useRef(null);

  // 交棒候選人：從 roleGroups 中取出所有有 assignee 的角色，去重
  const handoffCandidates = useMemo(() => {
    const seen = new Set();
    const list = [];
    Object.entries(roleGroups).forEach(([role, group]) => {
      if (group.assigneeId && !seen.has(group.assigneeId)) {
        seen.add(group.assigneeId);
        const user = usersMap[group.assigneeId];
        list.push({
          userId: group.assigneeId,
          name: user?.name || null,
          email: user?.email || null,
          avatarUrl: user?.avatar_url || null,
          roleType: role,
        });
      }
    });
    return list;
  }, [roleGroups, usersMap]);

  if (!deliverable) {
    return (
      <div className="dl-main-content">
        <div className="dl-content-empty">
          <p>選擇左側的交付項目來查看內容</p>
        </div>
      </div>
    );
  }

  const d = deliverable;
  const items = d.deliverable_items || [];
  const doneCount = items.filter((i) => i.is_completed).length;
  const totalCount = items.length;
  const percent = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const handleAddRole = () => {
    if (!newRoleName.trim()) return;
    onAddRoleSection(d.id, newRoleName.trim(), null);
    setNewRoleName('');
    setAddingRole(false);
  };

  const handleHandoffSelect = (candidate) => {
    setShowHandoff(false);
    onHandoff?.(d.id, candidate.userId, candidate.email);
  };

  const handleHandoffClear = () => {
    setShowHandoff(false);
    onHandoff?.(d.id, null, null);
  };

  const currentAssignee = d.current_assignee_id ? usersMap[d.current_assignee_id] : null;

  return (
    <div className="dl-main-content">
      {/* Header */}
      <div className={`dl-content-header ${d.qa_status || ''}`} data-tour="dl-content-header">
        <div className="dl-content-title-row">
          <span className="dl-content-title">{d.title}</span>
          <div className="dl-content-actions">
            {/* 交棒按鈕 */}
            <div className="dl-handoff-wrap">
              <button
                ref={handoffBtnRef}
                className={`dl-handoff-btn ${d.current_assignee_id ? 'has-assignee' : ''}`}
                onClick={() => setShowHandoff(!showHandoff)}
                title={currentAssignee ? `目前負責：${currentAssignee.name || currentAssignee.email}` : '交棒'}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <path d="M2 7h8M7 4l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>
                  {currentAssignee
                    ? `${currentAssignee.name || currentAssignee.email}`
                    : '交棒'}
                </span>
              </button>
              {showHandoff && (
                <HandoffPopover
                  anchorRef={handoffBtnRef}
                  candidates={handoffCandidates}
                  currentAssigneeId={d.current_assignee_id}
                  onSelect={handleHandoffSelect}
                  onClear={handleHandoffClear}
                  onClose={() => setShowHandoff(false)}
                />
              )}
            </div>
            <button
              className={`dl-qa-btn pass ${d.qa_status === 'success' ? 'active' : ''}`}
              onClick={() => onQaToggle(d, 'success')}
              title="QA Pass"
            >
              Pass
            </button>
            <button
              className={`dl-qa-btn fail ${d.qa_status === 'fail' ? 'active' : ''}`}
              onClick={() => {
                onQaToggle(d, 'fail');
                // Fail 時自動打開交棒 popover（從 fail → null 的 toggle 除外）
                if (d.qa_status !== 'fail') {
                  setTimeout(() => setShowHandoff(true), 100);
                }
              }}
              title="QA Fail"
            >
              Fail
            </button>
            {totalCount > 0 && (
              <span className="dl-content-progress-text">
                {doneCount}/{totalCount} ({percent}%)
              </span>
            )}
          </div>
        </div>
        <div className="dl-content-progress-bar">
          <div className="bar-fill" style={{ width: `${percent}%` }} />
        </div>
      </div>

      {/* Description */}
      <DeliverableDescription
        key={d.id}
        deliverableId={d.id}
        description={d.description || ''}
        onSave={onDescriptionChange}
      />

      {/* Role Card Grid */}
      <div className="dl-role-card-grid" data-tour="dl-role-card-grid">
        {Object.entries(roleGroups).map(([role, group]) => {
          const user = group.assigneeId ? usersMap[group.assigneeId] : null;
          return (
            <RoleCard
              key={role}
              role={role}
              assigneeId={group.assigneeId}
              assigneeName={user?.name || user?.email}
              assigneeAvatar={user?.avatar_url}
              items={group.items}
              onClick={() => onOpenPanel(d.id, role)}
              onDelete={() => onDeleteRole(d.id, role)}
            />
          );
        })}

        {/* Add Role Card */}
        {addingRole ? (
          <div className="dl-add-role-card expanded">
            <div className="dl-add-role-presets">
              {PRESET_ROLES.filter((r) => !Object.keys(roleGroups).includes(r)).map((r) => (
                <button
                  key={r}
                  className={`dl-preset-chip ${newRoleName === r ? 'active' : ''}`}
                  onClick={() => setNewRoleName(newRoleName === r ? '' : r)}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="dl-add-role-fields">
              <div className="dl-add-role-input-wrap">
                <label className="dl-field-label">角色</label>
                <input
                  ref={roleInputRef}
                  className="dl-add-role-input"
                  placeholder="輸入角色名稱..."
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.nativeEvent.isComposing) return;
                    if (e.key === 'Enter' && newRoleName.trim()) handleAddRole();
                    if (e.key === 'Escape') { setAddingRole(false); setNewRoleName(''); }
                  }}
                />
              </div>
            </div>
            <div className="dl-add-role-actions">
              <button
                className="dl-add-role-confirm"
                onClick={handleAddRole}
                disabled={!newRoleName.trim()}
              >
                新增
              </button>
              <button
                className="dl-add-role-cancel"
                onClick={() => { setAddingRole(false); setNewRoleName(''); }}
              >
                取消
              </button>
            </div>
          </div>
        ) : (
          <button
            className="dl-add-role-card"
            data-tour="dl-add-role-card"
            onClick={() => {
              setAddingRole(true);
              setNewRoleName('');
              setTimeout(() => roleInputRef.current?.focus(), 0);
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M10 4v12M4 10h12" strokeLinecap="round" />
            </svg>
            <span>新增角色</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Deliverable Description ─────────────

const DeliverableDescription = ({ deliverableId, description, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(description);

  const handleEdit = () => {
    setDraft(description);
    setEditing(true);
  };

  const handleSave = () => {
    setEditing(false);
    if (draft !== description) {
      onSave(deliverableId, draft);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft(description);
  };

  return (
    <div className="dl-description">
      <div className="dl-description-header">
        <span className="dl-description-label">說明</span>
        {!editing && (
          <button className="dl-description-edit-btn" onClick={handleEdit}>
            {description ? '編輯' : '新增說明'}
          </button>
        )}
        {editing && (
          <div className="dl-description-actions">
            <button className="dl-description-save" onClick={handleSave}>儲存</button>
            <button className="dl-description-cancel" onClick={handleCancel}>取消</button>
          </div>
        )}
      </div>
      {editing ? (
        <Suspense fallback={<div className="dl-description-loading">載入編輯器...</div>}>
          <MarkdownEditor
            value={draft}
            onChange={setDraft}
            placeholder="輸入交付項目說明..."
            height="100%"
          />
        </Suspense>
      ) : description ? (
        <div className="dl-description-viewer">
          <MarkdownViewer content={description} />
        </div>
      ) : null}
    </div>
  );
};

export default DeliverableContent;
